import { QqModel } from "../model/qq.model";
import { QqBundleModel } from "../model/qq-bundle.model";
import { OptionRangeModel } from "../model/option-range.model";
import { QqRecommendationResultModel } from "../model/qq-recommendation-result.model";
import _ from "lodash";
import { OptionProcessorService } from "./option-processor.service";
import { ControlsBundleMap } from "../message-interop/qq-state.type";
import { EquationProcessorService } from "./equation-processor.service";

export class QqBundleService {
  constructor(private bundle: QqBundleModel) {}

  fillDefault(questions: QqBundleModel): {
    currentOptions: { [key: string]: string };
    currentValues: { [key: string]: string[] };
    ctrlPos: { [key: string]: any };
    ctrlBundle: ControlsBundleMap;
  } {
    const currentOptions: { [key: string]: string } = {};
    const currentValues: { [key: string]: string[] } = {};
    const ctrlPos: { [key: string]: any } = {};
    const ctrlBundle: ControlsBundleMap = {};

    const ops = new OptionProcessorService(
      questions,
      ctrlBundle,
      currentOptions,
      currentValues,
      ctrlPos
    );
    const equ = new EquationProcessorService(
      this.bundle,
      ctrlBundle,
      currentOptions
    );

    questions.groups.forEach((group) => {
      group.qq.forEach((qq) => {
        if (qq.visible === undefined || qq.visible) {
          ops.fillDefault(qq);
          if (
            qq.optionsValue &&
            _.isString(qq.optionsValue[currentOptions[qq.key]])
          )
            equ.processEquation(
              qq,
              qq.optionsValue[currentOptions[qq.key]] as string
            );
        }
      });
    });

    return {
      currentOptions,
      currentValues,
      ctrlPos,
      ctrlBundle,
    };
  }

  private _separateRecommendationAndAlsoGood(result: string[]) {
    const recommendation: string[] = [];
    const alsoGood: string[] = [];

    function checkPush(checkArray: string[], array: string[], item: string) {
      if (checkArray.indexOf(item) === -1) array.push(item);
    }

    function processRecommendationItem(
      isRecommendation: boolean,
      item: string
    ) {
      if (result.indexOf(item) >= 0) {
        isRecommendation
          ? checkPush(recommendation.concat(alsoGood), recommendation, item)
          : checkPush(recommendation.concat(alsoGood), alsoGood, item);
      }
    }

    this.bundle.recommendationOrder.forEach((item) => {
      const isRecommendation = recommendation.length === 0;
      if (Array.isArray(item)) {
        item.forEach((i) => processRecommendationItem(isRecommendation, i));
      } else {
        processRecommendationItem(isRecommendation, item);
      }
    });

    return {
      recommendation: recommendation,
      alsoGood: alsoGood.slice(
        0,
        Math.max(0, this.bundle.maxResults - recommendation.length)
      ),
    };
  }

  getFinalQuestionIfExist(currentOptions: {
    [key: string]: string;
  }): QqModel | undefined {
    let rValue: QqModel | undefined = undefined;

    this.bundle.groups.some((group) => {
      return group.qq.some((gqq) => {
        const controlOption = currentOptions[gqq.key];
        if (gqq.final && gqq.final.includes(controlOption)) {
          rValue = gqq;
          return true;
        }

        return false;
      });
    });

    return rValue;
  }

  getFinalRecommendationIfExist(
    values: {
      [key: string]: string[];
    },
    currentOptions: { [key: string]: string }
  ): QqRecommendationResultModel | undefined {
    let recommendation = [] as string[];

    this.bundle.groups.some((g) =>
      g.qq.some((gqq) => {
        const k = gqq.key;
        if (values[k] && Array.isArray(values[k]) && values[k].length !== 0) {
          const qq = this.getControl(k);
          if (qq?.final && qq.final.indexOf(currentOptions[k]) > -1) {
            recommendation = values[k];
            return true;
          }
        }

        return false;
      })
    );

    return recommendation.length > 0
      ? this._separateRecommendationAndAlsoGood(recommendation)
      : undefined;
  }

  getRecommendations(
    values: {
      [key: string]: string[];
    },
    currentOptions: { [key: string]: string }
  ): QqRecommendationResultModel {
    const extended = {} as { [key: string]: Array<string> };

    Object.keys(values).forEach((k) => {
      if (!values[k] || !Array.isArray(values[k]) || values[k].length === 0)
        return (extended[k] = values[k]);

      const qq = this.getControl(k);
      let needsExtension = qq?.extend === undefined || qq.extend;

      if (qq?.noExtend && qq.noExtend.includes(currentOptions[k])) {
        needsExtension = false;
      }

      if (needsExtension) {
        const rightMost = values[k][values[k].length - 1];
        const extendFromIndex = this.bundle.extendValues.indexOf(rightMost);
        if (extendFromIndex >= 0) {
          const extension = this.bundle.extendValues.slice(extendFromIndex + 1);
          extended[k] = values[k].concat(extension);
        }
      } else {
        extended[k] = values[k];
      }
    }, {} as { [key: string]: string[] });

    const arrays = Object.values(extended).reduce((r, v) => {
      if (Array.isArray(v) && v.length > 0) {
        r.push(v);
      }

      return r;
    }, [] as string[][]);

    const result = _.intersection(...arrays);
    return this._separateRecommendationAndAlsoGood(result);
  }

  getSliderRangeByValue(
    value: number,
    slider: QqModel
  ): OptionRangeModel | undefined {
    if (!slider.optionsRange) return undefined;

    const selectedOption = slider.optionsRange.find((option) => {
      if (option.range[0] <= value && value <= option.range[1]) {
        return true;
      }
      return false;
    });

    return selectedOption;
  }

  getControl(key: string): QqModel | undefined {
    let control = undefined;

    this.bundle.groups.some((group) =>
      group.qq.some((item) => {
        if (item.key === key) {
          control = item;
          return true;
        }
        return false;
      })
    );

    return control;
  }
}
